import styles from '../CmsBusinessJourneyWizard.module.scss';
import { useMedia, useTracking } from '@components/hooks';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Content,
  Heading,
  Link,
  ToggleCard,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';
import { useCheckboxStore } from '@ariakit/react';

const PromotionCard = ({ headingLevel = 3, title, promotionText, cta }) => (
  <Card className={styles.promotionCardContainer}>
    <CardBody>
      <div className={styles.contentContainer}>
        <Heading
          as={getHeadingTag(headingLevel)}
          className={styles.promotionHeading}
          size="h6"
          theme="contrast"
        >
          {title}
        </Heading>
        <Content
          className={styles.promotionContent}
          noSpacing
          size="md"
          withParser
        >
          {promotionText}
        </Content>
      </div>
      <Button render={<Link target={cta.target} to={cta.url} />} theme="ghost">
        {cta.title}
      </Button>
    </CardBody>
  </Card>
);

const sortCards = (a, b) => {
  if (a.cardType === 'promotion' && b.cardType !== 'promotion') {
    return -1;
  }
  if (a.cardType !== 'promotion' && b.cardType === 'promotion') {
    return 1;
  }
  if (a.hasBadge && !b.hasBadge) {
    return -1;
  }
  if (!a.hasBadge && b.hasBadge) {
    return 1;
  }
  return 0;
};

const ToggleCardsList = ({
  cards,
  headingLevel,
  storage,
  setStorage,
  setStepNumber,
}) => {
  const track = useTracking();
  const isMobile = useMedia('(max-width: 767px)');
  const [showAllCards, setShowAllCards] = useState(true);
  const [value, setValue] = useState(
    storage?.selectedProducts?.map(
      selectedProduct => selectedProduct.keyword,
    ) || [],
  );
  const checkbox = useCheckboxStore({ value, setValue });

  useEffect(() => {
    if (isMobile && cards.length > 3) {
      setShowAllCards(false);
      return;
    }

    setShowAllCards(true);
  }, [isMobile]);

  useEffect(() => {
    if (storage.answers.length === 0 && storage.selectedProducts.length === 0) {
      setValue([]);
      setStepNumber(0);
    }
  }, [storage.answers.length]);

  useEffect(() => {
    let tempSelectedProducts = [];
    if (value.length > storage?.selectedProducts?.length) {
      const toggledCard = cards.find(
        card => card.keyword === value[value.length - 1],
      );
      const foundLink = toggledCard.links.find(link =>
        storage?.answers.every(answer =>
          link.condition.includes(answer.keyword),
        ),
      );

      tempSelectedProducts = [
        ...(storage?.selectedProducts || []),
        {
          label: toggledCard.cardTitle,
          keyword: toggledCard.keyword,
          link:
            foundLink?.url || 'https://www.wienenergie.at/business/produkte/',
        },
      ];

      if (tempSelectedProducts) {
        track({
          event: 'B2BProduktWegweiser_addProduct',
          answers: storage?.answers.map(answ => answ?.title.trim()),
          products: tempSelectedProducts[tempSelectedProducts.length - 1].link,
          step: 'add',
        });
      }
      const newStorage = {
        ...storage,
        emailSent: false,
        pageLink: window?.location?.href,
        selectedProducts: [
          ...(storage?.selectedProducts || []),
          {
            label: toggledCard.cardTitle,
            keyword: toggledCard.keyword,
            link:
              foundLink?.url || 'https://www.wienenergie.at/business/produkte/',
          },
        ],
      };
      // default link when no links are available for current selection
      setStorage(newStorage);
    } else if (value.length < storage?.selectedProducts?.length) {
      const newStorage = {
        ...storage,
        emailSent: false,
        selectedProducts: storage?.selectedProducts.filter(selectedProduct =>
          value.includes(selectedProduct.keyword),
        ),
      };

      setStorage(newStorage);
    }
  }, [value.length]);

  return (
    <>
      <div className={classNames(styles.cardsContainer, styles.isToggle)}>
        {cards
          .sort(sortCards)
          .slice(0, !showAllCards ? 3 : cards?.length)
          .map(card => {
            switch (card.cardType) {
              case 'toggle':
                return (
                  <div className={styles.badgeContainer} key={card.cardTitle}>
                    {card.hasBadge && (
                      <Badge
                        className={styles.badge}
                        size="large"
                        theme="dimmed"
                        variant="normal"
                      >
                        <span>{card.badgeLabel}</span>
                      </Badge>
                    )}
                    <ToggleCard
                      glyph={card.icon}
                      headingLevel={headingLevel}
                      image={card.imageIcon}
                      legalText={card.legalText}
                      store={checkbox}
                      title={card.cardTitle}
                      toggleLabel={card.toggleLabel || 'Benötige ich'}
                      value={card.keyword}
                    />
                  </div>
                );
              case 'promotion':
                return (
                  <PromotionCard
                    cta={card.cta}
                    headingLevel={headingLevel}
                    key={card.cardTitle}
                    promotionText={card.promotionText}
                    title={card.cardTitle}
                  />
                );
              default:
                return null;
            }
          })}
      </div>
      {!showAllCards && (
        <Button isElevated onClick={() => setShowAllCards(true)}>
          Mehr laden
        </Button>
      )}
    </>
  );
};

export default ToggleCardsList;
